import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API, BACKEND_URL, useAuth } from "../../App";
import { toast } from "sonner";
import {
  Gift, Users, Search, Plus, Trash2, Play, ExternalLink, Copy,
  History, Trophy, XCircle, CheckCircle, UserPlus, GripVertical,
  SkipForward, Edit2, Save, X, ListOrdered, Settings, Award
} from "lucide-react";

export default function CoupleRaffleController() {
  const { wedding } = useOutletContext();
  const { user } = useAuth();
  const [pool, setPool] = useState([]);
  const [manualParticipants, setManualParticipants] = useState([]);
  const [draws, setDraws] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [settings, setSettings] = useState({ unique_winners_only: true });
  const [search, setSearch] = useState("");
  const [newParticipant, setNewParticipant] = useState("");
  const [newPrizeName, setNewPrizeName] = useState("");
  const [newPrizeDesc, setNewPrizeDesc] = useState("");
  const [editingPrize, setEditingPrize] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [activeDraw, setActiveDraw] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("queue"); // queue, pool, history
  const pollRef = useRef(null);

  const token = localStorage.getItem("token");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const weddingId = wedding?.wedding_id;

  const fetchAll = async () => {
    if (!weddingId) return;
    try {
      const [poolRes, manualRes, drawsRes, prizesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/raffle/${weddingId}/pool?exclude_winners=${settings.unique_winners_only}`, { headers }),
        axios.get(`${API}/raffle/${weddingId}/participants`, { headers }),
        axios.get(`${API}/raffle/${weddingId}/draws`, { headers }),
        axios.get(`${API}/raffle/${weddingId}/prizes`, { headers }),
        axios.get(`${API}/raffle/${weddingId}/settings`, { headers }),
      ]);
      setPool(poolRes.data);
      setManualParticipants(manualRes.data);
      setDraws(drawsRes.data);
      setPrizes(prizesRes.data);
      setSettings(settingsRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, [weddingId]);

  // Re-fetch pool when settings change
  useEffect(() => {
    if (!weddingId || loading) return;
    axios.get(`${API}/raffle/${weddingId}/pool?exclude_winners=${settings.unique_winners_only}`, { headers })
      .then(r => setPool(r.data)).catch(() => {});
  }, [settings.unique_winners_only]);

  // Poll active draw
  useEffect(() => {
    if (!activeDraw || !weddingId) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/raffle/${weddingId}/draws/${activeDraw.draw_id}`, { headers });
        setActiveDraw(r.data);
        if (r.data.status === "completed" || r.data.status === "cancelled") {
          clearInterval(pollRef.current);
          setActiveDraw(null);
          setIsDrawing(false);
          fetchAll();
        }
      } catch {}
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [activeDraw?.draw_id, weddingId]);

  // --- Prize Queue Actions ---
  const addPrize = async () => {
    if (!newPrizeName.trim()) return;
    try {
      await axios.post(`${API}/raffle/${weddingId}/prizes`, { prize_name: newPrizeName.trim(), prize_description: newPrizeDesc.trim() || null }, { headers });
      setNewPrizeName("");
      setNewPrizeDesc("");
      toast.success("Prize added");
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  const updatePrize = async (prizeId) => {
    try {
      await axios.put(`${API}/raffle/${weddingId}/prizes/${prizeId}`, { prize_name: editName.trim(), prize_description: editDesc.trim() || null }, { headers });
      setEditingPrize(null);
      toast.success("Updated");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deletePrize = async (prizeId) => {
    try {
      await axios.delete(`${API}/raffle/${weddingId}/prizes/${prizeId}`, { headers });
      toast.success("Deleted");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); }
  };

  const skipPrize = async (prizeId) => {
    try {
      await axios.put(`${API}/raffle/${weddingId}/prizes/${prizeId}`, { status: "skipped" }, { headers });
      toast.success("Prize skipped");
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  const movePrize = async (index, direction) => {
    const newPrizes = [...prizes];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newPrizes.length) return;
    [newPrizes[index], newPrizes[swapIdx]] = [newPrizes[swapIdx], newPrizes[index]];
    const order = newPrizes.map(p => p.prize_id);
    try {
      await axios.put(`${API}/raffle/${weddingId}/prizes/reorder`, { order }, { headers });
      fetchAll();
    } catch { toast.error("Failed to reorder"); }
  };

  // --- Draw Actions ---
  const drawNext = async () => {
    setIsDrawing(true);
    try {
      const r = await axios.post(`${API}/raffle/${weddingId}/draw-next`, {}, { headers });
      setActiveDraw(r.data);
      toast.success(`Draw started! Winner locked: ${r.data.winner_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draw failed");
      setIsDrawing(false);
    }
  };

  const revealDraw = async (drawId) => {
    try { await axios.put(`${API}/raffle/${weddingId}/draws/${drawId}/reveal`, {}, { headers }); toast.success("Revealed"); fetchAll(); } catch { toast.error("Failed"); }
  };
  const completeDraw = async (drawId) => {
    try { await axios.put(`${API}/raffle/${weddingId}/draws/${drawId}/complete`, {}, { headers }); toast.success("Completed"); setActiveDraw(null); setIsDrawing(false); fetchAll(); } catch { toast.error("Failed"); }
  };
  const cancelDraw = async (drawId) => {
    try { await axios.put(`${API}/raffle/${weddingId}/draws/${drawId}/cancel`, {}, { headers }); toast.success("Cancelled"); setActiveDraw(null); setIsDrawing(false); fetchAll(); } catch { toast.error("Failed"); }
  };

  // --- Manual Participants ---
  const addManual = async () => {
    if (!newParticipant.trim()) return;
    try { await axios.post(`${API}/raffle/${weddingId}/participants`, { display_name: newParticipant.trim() }, { headers }); setNewParticipant(""); toast.success("Added"); fetchAll(); } catch { toast.error("Failed"); }
  };
  const removeManual = async (id) => {
    try { await axios.delete(`${API}/raffle/${weddingId}/participants/${id}`, { headers }); toast.success("Removed"); fetchAll(); } catch { toast.error("Failed"); }
  };

  // --- Settings ---
  const toggleUniqueWinners = async () => {
    const newVal = !settings.unique_winners_only;
    try {
      await axios.put(`${API}/raffle/${weddingId}/settings`, { unique_winners_only: newVal }, { headers });
      setSettings(s => ({ ...s, unique_winners_only: newVal }));
      toast.success(newVal ? "Unique winners only" : "Repeat winners allowed");
    } catch { toast.error("Failed"); }
  };

  const displayUrl = wedding ? `${window.location.origin}/${wedding.slug}/raffle` : "";
  const copyDisplayUrl = () => { navigator.clipboard.writeText(displayUrl); toast.success("Copied!"); };

  const nextPending = prizes.find(p => p.status === "pending");
  const drawnCount = prizes.filter(p => p.status === "drawn").length;
  const filteredPool = pool.filter(p => p.display_name.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="p-8 flex items-center justify-center min-h-[400px]"><div className="w-10 h-10 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-8 max-w-6xl" data-testid="raffle-controller">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory" data-testid="raffle-title">Wedding Raffle</h1>
          <p className="text-wedding-ivory/50 text-sm mt-1">Multi-round raffle controller</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={copyDisplayUrl} className="flex items-center gap-2 px-4 py-2 bg-wedding-subtle border border-white/10 text-wedding-ivory text-sm hover:border-wedding-gold/30 transition-colors" data-testid="copy-display-url">
            <Copy className="w-4 h-4" /> Display URL
          </button>
          <a href={displayUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-wedding-gold text-wedding-black text-sm font-bold hover:bg-wedding-ivory transition-colors" data-testid="open-display">
            <ExternalLink className="w-4 h-4" /> Open Display
          </a>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        <div className="p-3 bg-wedding-charcoal border border-white/5 text-center">
          <p className="text-2xl font-bold text-wedding-ivory" data-testid="pool-count">{pool.length}</p>
          <p className="text-wedding-ivory/40 text-xs">Eligible</p>
        </div>
        <div className="p-3 bg-wedding-charcoal border border-white/5 text-center">
          <p className="text-2xl font-bold text-wedding-ivory">{prizes.length}</p>
          <p className="text-wedding-ivory/40 text-xs">Prizes</p>
        </div>
        <div className="p-3 bg-wedding-charcoal border border-white/5 text-center">
          <p className="text-2xl font-bold text-wedding-gold">{drawnCount}</p>
          <p className="text-wedding-ivory/40 text-xs">Drawn</p>
        </div>
        <div className="p-3 bg-wedding-charcoal border border-white/5 text-center">
          <p className="text-2xl font-bold text-wedding-ivory">{prizes.filter(p => p.status === "pending").length}</p>
          <p className="text-wedding-ivory/40 text-xs">Remaining</p>
        </div>
      </div>

      {/* Draw Next Button — prominent */}
      <div className="mb-6 p-4 bg-wedding-charcoal border border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex-1">
          {nextPending ? (
            <div>
              <p className="text-wedding-ivory/50 text-xs uppercase tracking-widest">Next Prize</p>
              <p className="text-wedding-ivory font-serif text-lg">{nextPending.prize_name}</p>
              {nextPending.prize_description && <p className="text-wedding-ivory/40 text-sm">{nextPending.prize_description}</p>}
            </div>
          ) : prizes.length > 0 ? (
            <p className="text-wedding-ivory/40">All prizes have been drawn or skipped</p>
          ) : (
            <p className="text-wedding-ivory/40">Add prizes to the queue to get started</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {pool.length === 0 && nextPending && (
            <span className="text-red-400 text-sm">No eligible participants</span>
          )}
          <button
            onClick={drawNext}
            disabled={isDrawing || !nextPending || pool.length === 0}
            className="px-8 py-3 bg-wedding-gold text-wedding-black font-bold uppercase tracking-widest text-sm hover:bg-wedding-ivory transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="start-next-draw-btn"
          >
            <Play className="w-4 h-4" /> {isDrawing ? "Drawing..." : "Start Next Draw"}
          </button>
        </div>
      </div>

      {/* Active Draw Controls */}
      <AnimatePresence>
        {activeDraw && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="mb-6 p-4 bg-wedding-charcoal border border-wedding-gold/30 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-wedding-gold font-serif text-lg">Active: {activeDraw.prize_name}</p>
              <p className="text-wedding-ivory text-sm">Winner: <strong className="text-wedding-gold">{activeDraw.winner_name}</strong> <span className="text-wedding-ivory/40">({activeDraw.status})</span></p>
            </div>
            <div className="flex gap-2">
              {activeDraw.status === "running" && (
                <button onClick={() => revealDraw(activeDraw.draw_id)} className="px-4 py-2 bg-wedding-gold text-wedding-black text-sm font-bold hover:bg-wedding-ivory transition-colors" data-testid="reveal-btn">Reveal</button>
              )}
              {activeDraw.status === "revealed" && (
                <button onClick={() => completeDraw(activeDraw.draw_id)} className="px-4 py-2 bg-green-700 text-white text-sm font-bold hover:bg-green-600 transition-colors flex items-center gap-1" data-testid="complete-btn">
                  <CheckCircle className="w-4 h-4" /> Complete
                </button>
              )}
              {(activeDraw.status === "running" || activeDraw.status === "revealed") && (
                <button onClick={() => cancelDraw(activeDraw.draw_id)} className="px-3 py-2 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/10 transition-colors" data-testid="cancel-btn">
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tab selector */}
      <div className="flex gap-1 mb-4 border-b border-white/5">
        {[
          { id: "queue", icon: ListOrdered, label: "Prize Queue" },
          { id: "pool", icon: Users, label: `Pool (${pool.length})` },
          { id: "history", icon: History, label: "History" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition-colors ${tab === t.id ? "border-wedding-gold text-wedding-gold" : "border-transparent text-wedding-ivory/40 hover:text-wedding-ivory/60"}`}
            data-testid={`tab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* MAIN PANEL */}
        <div className="lg:col-span-2">
          {/* PRIZE QUEUE TAB */}
          {tab === "queue" && (
            <div className="space-y-4">
              {/* Add prize form */}
              <div className="p-4 bg-wedding-charcoal border border-white/5 flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-wedding-ivory/50 text-xs mb-1">Prize Name</label>
                  <input type="text" value={newPrizeName} onChange={e => setNewPrizeName(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addPrize()} placeholder="e.g. Grand Prize"
                    className="w-full px-3 py-2 bg-wedding-black border border-white/10 text-wedding-ivory text-sm focus:border-wedding-gold/50 outline-none" data-testid="new-prize-name" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-wedding-ivory/50 text-xs mb-1">Description (optional)</label>
                  <input type="text" value={newPrizeDesc} onChange={e => setNewPrizeDesc(e.target.value)}
                    placeholder="Brief description"
                    className="w-full px-3 py-2 bg-wedding-black border border-white/10 text-wedding-ivory text-sm focus:border-wedding-gold/50 outline-none" data-testid="new-prize-desc" />
                </div>
                <button onClick={addPrize} disabled={!newPrizeName.trim()} className="px-4 py-2 bg-wedding-gold text-wedding-black font-bold text-sm hover:bg-wedding-ivory transition-colors disabled:opacity-30 flex items-center gap-1" data-testid="add-prize-btn">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>

              {/* Prize list */}
              {prizes.length === 0 ? (
                <div className="p-8 text-center text-wedding-ivory/30">
                  <Gift className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No prizes in queue. Add prizes above to get started.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {prizes.map((prize, idx) => {
                    const isActive = nextPending?.prize_id === prize.prize_id && !isDrawing;
                    const isEditing = editingPrize === prize.prize_id;
                    return (
                      <motion.div key={prize.prize_id} layout
                        className={`p-3 border flex items-center gap-3 ${
                          prize.status === "drawn" ? "bg-wedding-gold/5 border-wedding-gold/20" :
                          prize.status === "skipped" ? "bg-white/3 border-white/5 opacity-50" :
                          prize.status === "cancelled" ? "bg-red-500/5 border-red-500/10 opacity-40" :
                          isActive ? "bg-wedding-charcoal border-wedding-gold/40" :
                          "bg-wedding-charcoal border-white/5"
                        }`} data-testid={`prize-${prize.prize_id}`}>
                        {/* Reorder grip */}
                        {prize.status === "pending" && (
                          <div className="flex flex-col gap-0.5">
                            <button onClick={() => movePrize(idx, -1)} disabled={idx === 0} className="text-wedding-ivory/20 hover:text-wedding-ivory/50 disabled:opacity-20 text-xs">&#9650;</button>
                            <button onClick={() => movePrize(idx, 1)} disabled={idx === prizes.length - 1} className="text-wedding-ivory/20 hover:text-wedding-ivory/50 disabled:opacity-20 text-xs">&#9660;</button>
                          </div>
                        )}

                        {/* Order number */}
                        <span className={`w-8 text-center font-bold text-sm ${prize.status === "drawn" ? "text-wedding-gold" : "text-wedding-ivory/30"}`}>
                          {idx + 1}
                        </span>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <div className="flex gap-2">
                              <input type="text" value={editName} onChange={e => setEditName(e.target.value)}
                                className="flex-1 px-2 py-1 bg-wedding-black border border-white/10 text-wedding-ivory text-sm outline-none" data-testid="edit-prize-name" />
                              <input type="text" value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Desc"
                                className="flex-1 px-2 py-1 bg-wedding-black border border-white/10 text-wedding-ivory text-sm outline-none" />
                            </div>
                          ) : (
                            <>
                              <p className="text-wedding-ivory text-sm font-semibold truncate">{prize.prize_name}</p>
                              {prize.prize_description && <p className="text-wedding-ivory/40 text-xs truncate">{prize.prize_description}</p>}
                            </>
                          )}
                        </div>

                        {/* Status badge */}
                        <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${
                          prize.status === "drawn" ? "bg-wedding-gold/20 text-wedding-gold" :
                          prize.status === "pending" ? "bg-white/10 text-white/50" :
                          prize.status === "skipped" ? "bg-yellow-900/30 text-yellow-500" :
                          "bg-red-900/30 text-red-400"
                        }`}>{prize.status}</span>

                        {/* Winner name for drawn prizes */}
                        {prize.status === "drawn" && prize.linked_draw_id && (
                          <span className="text-wedding-gold text-xs flex items-center gap-1 flex-shrink-0">
                            <Trophy className="w-3 h-3" /> {draws.find(d => d.draw_id === prize.linked_draw_id)?.winner_name || ""}
                          </span>
                        )}

                        {/* Actions */}
                        {prize.status === "pending" && (
                          <div className="flex gap-1 flex-shrink-0">
                            {isEditing ? (
                              <>
                                <button onClick={() => updatePrize(prize.prize_id)} className="p-1.5 text-green-400 hover:bg-green-400/10 transition-colors" data-testid="save-edit-btn"><Save className="w-3.5 h-3.5" /></button>
                                <button onClick={() => setEditingPrize(null)} className="p-1.5 text-red-400 hover:bg-red-400/10 transition-colors"><X className="w-3.5 h-3.5" /></button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => { setEditingPrize(prize.prize_id); setEditName(prize.prize_name); setEditDesc(prize.prize_description || ""); }} className="p-1.5 text-wedding-ivory/30 hover:text-wedding-ivory/60 transition-colors" data-testid={`edit-prize-${prize.prize_id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                                <button onClick={() => skipPrize(prize.prize_id)} className="p-1.5 text-yellow-500/50 hover:text-yellow-500 transition-colors" title="Skip" data-testid={`skip-prize-${prize.prize_id}`}><SkipForward className="w-3.5 h-3.5" /></button>
                                <button onClick={() => deletePrize(prize.prize_id)} className="p-1.5 text-red-400/50 hover:text-red-400 transition-colors" data-testid={`delete-prize-${prize.prize_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                              </>
                            )}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* POOL TAB */}
          {tab === "pool" && (
            <div className="p-5 bg-wedding-charcoal border border-white/5">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-wedding-ivory/30" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search participants..."
                  className="w-full pl-9 pr-3 py-2 bg-wedding-black border border-white/10 text-wedding-ivory text-sm focus:border-wedding-gold/50 outline-none" data-testid="pool-search" />
              </div>
              <div className="space-y-1 max-h-[500px] overflow-y-auto">
                {filteredPool.map(p => (
                  <div key={p.id} className="flex items-center gap-3 py-2 px-2 bg-wedding-black/30 hover:bg-wedding-black/50 transition-colors">
                    {p.thumbnail_url ? (
                      <img src={p.thumbnail_url.startsWith("http") ? p.thumbnail_url : `${BACKEND_URL}${p.thumbnail_url}`} alt="" className="w-7 h-7 rounded-full object-cover" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-wedding-gold/10 flex items-center justify-center">
                        <span className="text-wedding-gold text-xs font-bold">{p.display_name[0]?.toUpperCase()}</span>
                      </div>
                    )}
                    <span className="text-wedding-ivory text-sm truncate flex-1">{p.display_name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${p.source === "tree" ? "bg-green-900/30 text-green-400" : "bg-blue-900/30 text-blue-400"}`}>{p.source}</span>
                  </div>
                ))}
                {filteredPool.length === 0 && <p className="text-wedding-ivory/30 text-sm text-center py-8">No participants found</p>}
              </div>
            </div>
          )}

          {/* HISTORY TAB */}
          {tab === "history" && (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {draws.length === 0 ? (
                <p className="text-wedding-ivory/30 text-sm text-center py-8 bg-wedding-charcoal border border-white/5 p-5">No draws yet</p>
              ) : draws.map(d => (
                <div key={d.draw_id} className={`p-4 border ${d.status === "completed" || d.status === "revealed" ? "border-wedding-gold/20 bg-wedding-gold/5" : d.status === "cancelled" ? "border-red-500/20 bg-red-500/5" : "border-white/5 bg-wedding-charcoal"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-wedding-ivory font-semibold">{d.prize_name}</p>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${d.status === "completed" ? "bg-green-900/30 text-green-400" : d.status === "revealed" ? "bg-wedding-gold/20 text-wedding-gold" : d.status === "cancelled" ? "bg-red-900/30 text-red-400" : "bg-white/10 text-white/50"}`}>{d.status}</span>
                  </div>
                  {d.winner_name && (d.status === "completed" || d.status === "revealed") && (
                    <p className="text-wedding-gold text-sm flex items-center gap-1"><Trophy className="w-3 h-3" /> {d.winner_name}</p>
                  )}
                  <p className="text-wedding-ivory/30 text-xs mt-1">Pool: {d.pool_count} | {new Date(d.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR */}
        <div className="lg:col-span-1 space-y-5">
          {/* Settings */}
          <div className="p-4 bg-wedding-charcoal border border-white/5 space-y-3">
            <h3 className="text-wedding-ivory text-sm font-semibold flex items-center gap-2">
              <Settings className="w-4 h-4 text-wedding-gold" /> Winner Policy
            </h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={settings.unique_winners_only} onChange={toggleUniqueWinners} className="accent-[#D4AF37]" data-testid="unique-winners-toggle" />
              <span className="text-wedding-ivory/60 text-sm">Unique winners only</span>
            </label>
            <p className="text-wedding-ivory/30 text-xs">
              {settings.unique_winners_only ? "Previous winners are excluded from future draws" : "Winners can win multiple prizes"}
            </p>
          </div>

          {/* Manual Participants */}
          <div className="p-4 bg-wedding-charcoal border border-white/5 space-y-3">
            <h3 className="text-wedding-ivory text-sm font-semibold flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-wedding-gold" /> Manual Participants
            </h3>
            <div className="flex gap-2">
              <input type="text" value={newParticipant} onChange={e => setNewParticipant(e.target.value)} onKeyDown={e => e.key === "Enter" && addManual()} placeholder="Guest name"
                className="flex-1 px-3 py-2 bg-wedding-black border border-white/10 text-wedding-ivory text-sm focus:border-wedding-gold/50 outline-none" data-testid="manual-participant-input" />
              <button onClick={addManual} disabled={!newParticipant.trim()} className="px-3 py-2 bg-wedding-gold text-wedding-black hover:bg-wedding-ivory transition-colors disabled:opacity-30" data-testid="add-participant-btn"><Plus className="w-4 h-4" /></button>
            </div>
            {manualParticipants.length > 0 && (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {manualParticipants.map(p => (
                  <div key={p.participant_id} className="flex items-center justify-between py-1.5 px-2 bg-wedding-black/50 text-sm">
                    <span className="text-wedding-ivory">{p.display_name}</span>
                    <button onClick={() => removeManual(p.participant_id)} className="text-red-400/60 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Winners Board */}
          {draws.filter(d => d.status === "completed" || d.status === "revealed").length > 0 && (
            <div className="p-4 bg-wedding-charcoal border border-white/5 space-y-2">
              <h3 className="text-wedding-ivory text-sm font-semibold flex items-center gap-2">
                <Award className="w-4 h-4 text-wedding-gold" /> Winners
              </h3>
              {draws.filter(d => d.status === "completed" || d.status === "revealed").map(d => (
                <div key={d.draw_id} className="flex items-center justify-between py-1 text-sm">
                  <span className="text-wedding-gold">{d.winner_name}</span>
                  <span className="text-wedding-ivory/40 truncate ml-2">{d.prize_name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
